import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  addBuyRate,
  addConsumable,
  addSellRate,
  updateConsumableItemRanks,
  updateSingleConsumableItem,
  updateRate,
} from 'api/consumablesApi';
import {
  addEquipmentBuyRate,
  addEquipmentCategory,
  addEquipmentSellRate,
  updateEquipmentCategoryItem,
  updateEquipmentItemRanks,
} from 'api/equipmentApi';
import { useSnackbar } from 'notistack';

export const useRateForm = (handleClose: () => void) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const {
    mutate: addConsumableSellRateMutate,
    isLoading: isAddingConsumableSellRate,
  } = useMutation(addSellRate, {
    onSuccess: async () => {
      enqueueSnackbar('Successfully saved consumable item', {
        variant: 'success',
      });
      await queryClient.invalidateQueries(['consumables']);
      await queryClient.invalidateQueries(['consumables-items']);
      handleClose();
    },
    onError: () => {
      enqueueSnackbar('Failed to save consumable item', { variant: 'error' });
    },
  });

  const {
    mutate: addConsumableBuyRateMutate,
    isLoading: isAddingConsumableBuyRate,
  } = useMutation(addBuyRate, {
    onSuccess: async () => {
      enqueueSnackbar('Successfully saved consumable item', {
        variant: 'success',
      });
      await queryClient.invalidateQueries(['consumables']);
      await queryClient.invalidateQueries(['consumables-items']);
      handleClose();
    },
    onError: () => {
      enqueueSnackbar('Failed to save consumable item', { variant: 'error' });
    },
  });

  // const { mutate: updateRateMutate } = useMutation(updateRate, {
  //   onSuccess: async () => {
  //     enqueueSnackbar('Successfully updated rate', { variant: 'success' });
  //     await queryClient.invalidateQueries(['consumables']);
  //     await queryClient.invalidateQueries(['consumables-items']);
  //     handleClose();
  //   },
  //   onError: () => {
  //     enqueueSnackbar('Failed to update rate', { variant: 'error' });
  //   },
  // });

  const { mutate: addConsumableMutate, isLoading: isAddingConsumable } =
    useMutation(addConsumable, {
      onSuccess: async () => {
        enqueueSnackbar('Successfully added consumable item', {
          variant: 'success',
        });
        await queryClient.invalidateQueries(['consumables']);
        await queryClient.invalidateQueries(['consumables-items']);
        handleClose();
      },
      onError: () => {
        enqueueSnackbar('Failed to update rate', { variant: 'error' });
      },
    });

  const {
    mutate: addEquipmentCategoryMutate,
    isLoading: isAddingEquipmentCategory,
  } = useMutation(addEquipmentCategory, {
    onSuccess: async () => {
      enqueueSnackbar('Successfully added equipment category', {
        variant: 'success',
      });
      await queryClient.invalidateQueries(['equipment']);
      await queryClient.invalidateQueries(['equipment-categories']);
      await queryClient.invalidateQueries(['equipment-rate-items']);
      handleClose();
    },
    onError: () => {
      enqueueSnackbar('Failed to update rate', { variant: 'error' });
    },
  });

  const {
    mutate: equipmentSellRateMutate,
    isLoading: isAddingEquipmentSellRate,
  } = useMutation(addEquipmentSellRate, {
    onSuccess: async () => {
      enqueueSnackbar('Successfully saved equipment sell rate', {
        variant: 'success',
      });
      await queryClient.invalidateQueries(['equipment-rate-items']);
      handleClose();
    },
    onError: () => {
      enqueueSnackbar('Failed to save equipment sell rate', {
        variant: 'error',
      });
    },
  });

  const {
    mutate: equipmentBuyRateMutate,
    isLoading: isAddingEquipmentBuyRate,
  } = useMutation(addEquipmentBuyRate, {
    onSuccess: async () => {
      enqueueSnackbar('Successfully saved equipment buy rate', {
        variant: 'success',
      });
      await queryClient.invalidateQueries(['equipment-rate-items']);
      handleClose();
    },
    onError: () => {
      enqueueSnackbar('Failed to save equipment buy rate', {
        variant: 'error',
      });
    },
  });

  const {
    mutate: updateEquipmentCategoryItems,
    isLoading: isUpdatingEquipmentCategoryItems,
  } = useMutation(updateEquipmentCategoryItem, {
    onSuccess: async () => {
      enqueueSnackbar('Successfully updated equipment category item', {
        variant: 'success',
      });
      await queryClient.invalidateQueries(['equipment-rate-items']);
      handleClose();
    },
    onError: () => {
      enqueueSnackbar('Failed to update equipment category item', {
        variant: 'error',
      });
    },
  });

  const {
    mutate: updateSingleConsumableItemMutate,
    isLoading: isUpdatingSingleConsumableItem,
  } = useMutation(updateSingleConsumableItem, {
    onSuccess: async () => {
      enqueueSnackbar('Successfully updated consumable item', {
        variant: 'success',
      });
      await queryClient.invalidateQueries(['consumables']);
      await queryClient.invalidateQueries(['consumables-items']);
      handleClose();
    },
    onError: () => {
      enqueueSnackbar('Failed to update consumable item', {
        variant: 'error',
      });
    },
  });

  const {
    mutate: updateConsumableItemRanksMutate,
    isLoading: isUpdatingConsumableItemRanks,
  } = useMutation(updateConsumableItemRanks, {
    onSuccess: async () => {
      enqueueSnackbar('Successfully updated consumable item', {
        variant: 'success',
      });
      await queryClient.invalidateQueries(['consumables']);
      await queryClient.invalidateQueries(['consumables-items']);
      handleClose();
    },
    onError: () => {
      enqueueSnackbar('Failed to update consumable item', {
        variant: 'error',
      });
    },
  });

  const {
    mutate: updateEquipmentItemRanksMutate,
    isLoading: isUpdatingEquipmentItemRanks,
  } = useMutation(updateEquipmentItemRanks, {
    onSuccess: async () => {
      enqueueSnackbar('Successfully updated equipment item ranks', {
        variant: 'success',
      });
      await queryClient.invalidateQueries(['equipment']);
      await queryClient.invalidateQueries(['equipment-categories']);
      await queryClient.invalidateQueries(['equipment-rate-items']);
      handleClose();
    },
    onError: () => {
      enqueueSnackbar('Failed to update equipment item ranks', {
        variant: 'error',
      });
    },
  });
  return {
    addConsumableSellRateMutate,
    addConsumableBuyRateMutate,
    // updateRateMutate,
    addConsumableMutate,
    addEquipmentCategoryMutate,
    equipmentSellRateMutate,
    equipmentBuyRateMutate,
    updateSingleConsumableItemMutate,
    updateEquipmentCategoryItems,
    updateConsumableItemRanksMutate,
    updateEquipmentItemRanksMutate,
    isAddingConsumable,
    isAddingConsumableSellRate,
    isAddingConsumableBuyRate,
    isAddingEquipmentCategory,
    isAddingEquipmentSellRate,
    isAddingEquipmentBuyRate,
    isUpdatingEquipmentCategoryItems,
    isUpdatingSingleConsumableItem,
    isUpdatingConsumableItemRanks,
    isUpdatingEquipmentItemRanks,
  };
};
